import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { CAMERA_MODEL, CameraService, TCameraModel } from '@monorepo/services';
import {
  IOperationsList,
  IDefectList,
  TBctxHeaderItem,
  TOperationIssueAndMarkedLocations,
  TOperationAndSelectedIssueList,
  TDefectiveFlowContainerResult,
  TOperationAnsIssue,
  TLocationCoordinates,
  TDefectiveOperationIssueAndSilhouetteMarkingInfo,
  TUpdateFilteredLists,
} from '@monorepo/types';
import { BCTXComputationHelper, EventUtil } from '@monorepo/utils';

@Component({
  selector: 'monorepo-defective-flow-container',
  templateUrl: './defective-flow-container.component.html',
  styleUrls: ['./defective-flow-container.component.scss'],
})
export class DefectiveFlowContainerComponent implements OnInit {
  @Input() isImageCapturingEnabled = false;
  @Input() bctxHeader: TBctxHeaderItem[] = [];
  #operationListVM: IOperationsList[] = [];
  @Input() allOperationList: IOperationsList[] = [];
  @Input()
  get operationList() {
    return this.#operationListVM;
  }
  set operationList(operationList: IOperationsList[]) {
    this.#operationListVM = operationList;
    this.handleOperationClearAll();
  }
  @Input() isRecentDefectsSelection = false;
  @Input() recentDefects: TOperationAnsIssue[] = [];
  @Input() allIssueList: IDefectList[] = [];
  #issueListVM: IDefectList[] = [];
  @Input()
  get issueList() {
    return this.#issueListVM;
  }
  set issueList(issueList: IDefectList[]) {
    this.#issueListVM = issueList;
    this.handleIssueClearAll();
  }
  @Input() silhouette_front = '';
  @Input() silhouette_back = '';
  @Input() silhoutteId = 'DEFAULT';
  @Output() cancel = new EventEmitter<'CANCEL'>();
  @Output() done = new EventEmitter<TDefectiveFlowContainerResult>();
  @Output() filterLists = new EventEmitter<TUpdateFilteredLists>();

  CAMERA_MODEL = CAMERA_MODEL.identity(EventUtil.getBucketIdFromUniqueEventId(BCTXComputationHelper.getUniqueVId()));

  isOperationSelectionStep: boolean = true;
  isIssueSelectionStep: boolean = false;
  isMarkLocationStep: boolean = false;
  isRecentDefectiveSelectionStep: boolean = false;

  selectedOperationList: IOperationsList[] = [];
  recentDefectiveList: TOperationIssueAndMarkedLocations[] = [];
  selectedRecentDefectiveList: TOperationIssueAndMarkedLocations[] = [];
  operationAndSelectedIssueList: TOperationAndSelectedIssueList[] = [];
  operationIssueAndMarkedLocationsList: TOperationIssueAndMarkedLocations[] = [];
  #bucketInfo: TCameraModel['bucketInfo'] = this.CAMERA_MODEL.bucketInfo;

  constructor(private cameraService: CameraService, private modalCtrl: ModalController) {}

  ngOnInit(): void {
    if (this.isRecentDefectsSelection) {
      this.isOperationSelectionStep = false;
      this.isRecentDefectiveSelectionStep = true;
      this.#createRecentDefectiveList();
    }
  }

  async openCamera() {
    this.cameraService.openModal(this.CAMERA_MODEL).then((v) => {
      console.log('Dismiss Data', v.data);
      if (v.data) this.#bucketInfo = v.data;
    });
  }

  #createRecentDefectiveList() {
    this.recentDefectiveList = this.recentDefects.map((item) => {
      return {
        ...item,
        front_locations: [],
        back_locations: [],
      };
    });
  }

  #getDefectiveOperationIssueAndSilhouetteMarkingInfoItem(
    operation: IOperationsList,
    issue: IDefectList,
    location: TLocationCoordinates,
    isFrontLocation: boolean,
  ) {
    const defectiveOperationIssueAndSilhouetteMarkingInfoItem: TDefectiveOperationIssueAndSilhouetteMarkingInfo = {
      operation: {
        category_id: operation.category_id,
        name: operation.name,
        display: true, // TODO [RISHIKESH] confirm with Aditya
        group_name: '',
      },
      issues: {
        category_id: issue.category_id,
        name: issue.name,
      },
      silhouette: {
        x: location.x,
        y: location.y,
        image: isFrontLocation ? 'front image' : 'back image',
        ...(this.silhoutteId !== 'DEFAULT' && { shillohutte_id: this.silhoutteId }),
      },
    };
    return defectiveOperationIssueAndSilhouetteMarkingInfoItem;
  }

  handleCancel() {
    this.cancel.emit('CANCEL');
  }

  handleOperationSelectionNext(operationList: IOperationsList[]) {
    const updatedOperationAndSelectedIssueList: TOperationAndSelectedIssueList[] = [];
    let isOperationAndSelectedIssueListChanged = false;
    operationList.forEach((item) => {
      const operationAndSelectedIssueListItem = this.operationAndSelectedIssueList.find(
        (operationAndSelectedIssueListItem) =>
          operationAndSelectedIssueListItem.operation.category_id === item.category_id,
      );
      if (operationAndSelectedIssueListItem)
        updatedOperationAndSelectedIssueList.push(operationAndSelectedIssueListItem);
      else {
        isOperationAndSelectedIssueListChanged = true;
        updatedOperationAndSelectedIssueList.push({ operation: item, selectedIssue: [] });
      }
    });
    if (isOperationAndSelectedIssueListChanged || this.operationAndSelectedIssueList.length !== operationList.length) {
      this.operationAndSelectedIssueList = updatedOperationAndSelectedIssueList.map((item) => {
        return {
          ...item,
          selectedIssue: [],
        };
      });
    } else this.operationAndSelectedIssueList = updatedOperationAndSelectedIssueList;
    this.isOperationSelectionStep = false;
    this.isIssueSelectionStep = true;
  }

  handleRecentDefectiveSelectionNext(operationIssueAndMarkedLocations: TOperationIssueAndMarkedLocations[]) {
    this.operationIssueAndMarkedLocationsList = operationIssueAndMarkedLocations;
    this.selectedRecentDefectiveList = operationIssueAndMarkedLocations;
    this.isRecentDefectiveSelectionStep = false;
    this.isMarkLocationStep = true;
  }

  handleOperationOrRejectReasonSelectionNext(list: any) {
    if (this.isRecentDefectsSelection)
      this.handleRecentDefectiveSelectionNext(list as TOperationIssueAndMarkedLocations[]);
    else this.handleOperationSelectionNext(list as IOperationsList[]);
  }

  handleIssueSelectionBack(operationAndSelectedIssueList: TOperationAndSelectedIssueList[]) {
    this.operationAndSelectedIssueList = operationAndSelectedIssueList;
    this.selectedOperationList = this.operationAndSelectedIssueList.map((item) => item.operation);
    this.isIssueSelectionStep = false;
    this.isOperationSelectionStep = true;
  }

  handleIssueSelectionNext(operationAndSelectedIssueList: TOperationAndSelectedIssueList[]) {
    this.operationAndSelectedIssueList = operationAndSelectedIssueList;
    const updatedOperationIssueAndMarkedLocationsList: TOperationIssueAndMarkedLocations[] = [];
    let isOperationIssueAndMarkedLocationsListChanged = false;
    operationAndSelectedIssueList.forEach((item) => {
      item.selectedIssue.forEach((issue) => {
        const operationIssueAndMarkedLocationsListItem = this.operationIssueAndMarkedLocationsList.find(
          (operationIssueAndMarkedLocationsListItem) =>
            operationIssueAndMarkedLocationsListItem.operation.category_id === item.operation.category_id &&
            operationIssueAndMarkedLocationsListItem.issue.category_id === issue.category_id,
        );
        if (operationIssueAndMarkedLocationsListItem)
          updatedOperationIssueAndMarkedLocationsList.push(operationIssueAndMarkedLocationsListItem);
        else {
          isOperationIssueAndMarkedLocationsListChanged = true;
          updatedOperationIssueAndMarkedLocationsList.push({
            operation: item.operation,
            issue: issue,
            back_locations: [],
            front_locations: [],
          });
        }
      });
    });
    if (
      isOperationIssueAndMarkedLocationsListChanged ||
      this.operationIssueAndMarkedLocationsList.length !== updatedOperationIssueAndMarkedLocationsList.length
    ) {
      this.operationIssueAndMarkedLocationsList = updatedOperationIssueAndMarkedLocationsList.map((item) => {
        return {
          ...item,
          front_locations: [],
          back_locations: [],
        };
      });
    } else this.operationIssueAndMarkedLocationsList = updatedOperationIssueAndMarkedLocationsList;
    this.isIssueSelectionStep = false;
    this.isMarkLocationStep = true;
  }

  handleMarkLocationBack(operationIssueAndMarkedLocationsList: TOperationIssueAndMarkedLocations[]) {
    this.operationIssueAndMarkedLocationsList = operationIssueAndMarkedLocationsList;
    this.isMarkLocationStep = false;
    if (this.isRecentDefectsSelection) {
      this.selectedRecentDefectiveList = operationIssueAndMarkedLocationsList;
      this.isRecentDefectiveSelectionStep = true;
    } else this.isIssueSelectionStep = true;
  }

  handleDone(defectiveOperationIssueAndSilhouetteMarkedLocationList: TOperationIssueAndMarkedLocations[]) {
    const defectiveOperationIssueAndSilhouetteMarkingInfoList: TDefectiveOperationIssueAndSilhouetteMarkingInfo[] = [];
    defectiveOperationIssueAndSilhouetteMarkedLocationList.forEach((item) => {
      item.back_locations.forEach((location) =>
        defectiveOperationIssueAndSilhouetteMarkingInfoList.push(
          this.#getDefectiveOperationIssueAndSilhouetteMarkingInfoItem(item.operation, item.issue, location, false),
        ),
      );
      item.front_locations.forEach((location) =>
        defectiveOperationIssueAndSilhouetteMarkingInfoList.push(
          this.#getDefectiveOperationIssueAndSilhouetteMarkingInfoItem(item.operation, item.issue, location, true),
        ),
      );
    });
    console.log('Bucket info from defective-flow-container', this.#bucketInfo);
    this.done.emit({
      result: defectiveOperationIssueAndSilhouetteMarkingInfoList,
      defects: defectiveOperationIssueAndSilhouetteMarkedLocationList.map((item) => {
        return {
          operation: item.operation,
          issue: item.issue,
        };
      }),
      bucketInfo: this.#bucketInfo,
    });
  }

  handleOperationClearAll() {
    this.selectedOperationList = [];
    this.selectedRecentDefectiveList = [];
    this.handleIssueClearAll();
    this.handleMarkLocationClearAll();
  }

  onViewAll(categories: string[], isOperationListElseIssueList: boolean) {
    this.filterLists.emit({ filteredCategories: categories, isOperationListElseIssueList });
  }

  handleIssueClearAll() {
    this.operationAndSelectedIssueList = this.operationAndSelectedIssueList.map((item) => {
      return {
        ...item,
        selectedIssue: [],
      };
    });
    this.handleMarkLocationClearAll();
  }

  handleMarkLocationClearAll() {
    this.operationIssueAndMarkedLocationsList = this.operationIssueAndMarkedLocationsList.map((item) => {
      return {
        ...item,
        front_locations: [],
        back_locations: [],
      };
    });
  }
}
